import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCameraRetro, FaImages, FaRoute } from 'react-icons/fa';
import { getExperienceLine, getModuleTitle, getProgressPercent, nudge, playSoftTone } from './openingUtils';

const MemoryJourneyExperience = ({ modules, renderModules, openingMode, giftIntro = {} }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [openingMode, modules?.length]);

  const activeModule = modules[activeIndex] || modules[0];
  const progress = getProgressPercent(activeIndex, modules.length);
  const introTitle = giftIntro.title || 'Turn the album into a path';
  const introCopy = giftIntro.subtitle ||
    'The album becomes a path. Follow each stop, then jump back to any chapter from the map.';
  const safeModules = modules.slice(0, Math.max(0, modules.length));
  const journeyNodes = safeModules.map((module, index) => {
    const count = Math.max(safeModules.length, 1);
    const x = count === 1 ? 50 : 8 + (index * 84) / (count - 1);
    const wave = count === 1 ? 0 : Math.sin(index * 1.35) * 25;
    const y = Math.max(18, Math.min(82, 52 + wave));
    return { module, index, x, y };
  });
  const polylinePoints = journeyNodes.map((node) => `${node.x * 3.2},${node.y * 1.3}`).join(' ');

  return (
    <div className="w-full max-w-2xl mx-auto px-0 space-y-6">
      <div className="lumirae-opening-card mx-4">
        <div className="relative min-h-[260px] overflow-hidden p-6 text-center">
          <motion.div
            className="absolute -left-8 top-8 h-36 w-24 origin-right rotate-[-12deg] rounded-[1.5rem] border-4 border-white bg-white/70 shadow-xl"
            animate={{ rotateY: [0, -18, 0], rotate: [-12, -16, -12] }}
            transition={{ duration: 4.8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -right-8 bottom-8 h-36 w-24 origin-left rotate-[14deg] rounded-[1.5rem] border-4 border-white bg-white/70 shadow-xl"
            animate={{ rotateY: [0, 16, 0], rotate: [14, 18, 14] }}
            transition={{ duration: 5.1, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="relative mx-auto mb-5 flex h-36 w-28 origin-left items-center justify-center rounded-[2rem] border-4 border-white bg-white shadow-2xl"
            animate={{ rotateY: [0, -9, 0], y: [0, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <FaCameraRetro className="text-5xl" style={{ color: 'var(--lumirae-accent-primary)' }} />
            <div className="absolute bottom-6 h-2 w-16 rounded-full" style={{ backgroundColor: 'var(--lumirae-accent-soft)' }} />
          </motion.div>
          <div className="lumirae-opening-label relative flex items-center justify-center gap-2">
            <FaImages /> Photo Album
          </div>
          <h2 className="lumirae-opening-title relative mx-auto mt-3 max-w-xs text-3xl leading-tight">
            {introTitle}
          </h2>
          <p className="lumirae-opening-copy relative mx-auto mt-3 max-w-xs">
            {introCopy}
          </p>
          <div className="lumirae-opening-pill relative mt-5 inline-flex px-4 py-2 text-[8px] font-black uppercase tracking-[0.24em]">
            {modules.length} story stops detected
          </div>
        </div>
        <div className="p-5">
          <div className="lumirae-ds-panel mb-4 p-4 text-center">
            <FaRoute className="mx-auto mb-2 text-2xl" style={{ color: 'var(--lumirae-accent-primary)' }} />
            <div className="text-sm font-black uppercase" style={{ color: 'var(--lumirae-text-primary)' }}>
              {activeModule ? getModuleTitle(activeModule, activeIndex) : 'Your story path'}
            </div>
            <div className="lumirae-ds-meta mt-1">
              {getExperienceLine(activeIndex)}
            </div>
            <div className="lumirae-opening-progress mt-4 h-2">
              <motion.div className="h-full rounded-full" animate={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
        <div className="lumirae-opening-stage relative mx-5 mb-5 h-40 overflow-hidden p-5">
          <svg viewBox="0 0 320 130" className="absolute inset-0 h-full w-full">
            {journeyNodes.length > 1 ? (
              <>
                <polyline points={polylinePoints} fill="none" stroke="rgba(33,27,29,0.14)" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="10 12" />
                <motion.polyline
                  points={polylinePoints}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: progress / 100 }}
                />
              </>
            ) : (
              <circle cx="160" cy="66" r="34" fill="none" stroke="rgba(33,27,29,0.14)" strokeWidth="8" strokeDasharray="10 12" />
            )}
            <motion.path
              d=""
              fill="none"
            />
          </svg>
          {journeyNodes.map(({ module, index, x, y }) => {
            return (
              <button
                key={module.id || module._localId}
                onClick={() => {
                  nudge(16);
                  playSoftTone(520 + index * 42, 0.08);
                  setActiveIndex(index);
                }}
                className={`absolute flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 text-[10px] font-black shadow-xl transition-all ${
                  activeIndex === index ? 'scale-110 border-white text-white' : 'border-white bg-white'
                }`}
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  ...(activeIndex === index ?
                    { backgroundColor: 'var(--lumirae-accent-primary)' } :
                    { color: 'var(--lumirae-accent-primary)' }),
                }}
                aria-label={`Open memory moment ${index + 1}`}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
          {modules.map((module, index) => (
            <button
              key={`strip_${module.id || module._localId || index}`}
              onClick={() => {
                nudge(16);
                setActiveIndex(index);
              }}
              className={`min-w-[118px] rounded-[1.25rem] border px-3 py-3 text-left transition-all ${
                activeIndex === index ? 'border-white text-white shadow-lg' : 'lumirae-opening-pill'
              }`}
              style={activeIndex === index ? { backgroundColor: 'var(--lumirae-accent-primary)' } : undefined}
            >
              <span className="block text-[8px] font-black uppercase tracking-[0.24em] opacity-70">
                Stop {index + 1}
              </span>
              <span className="mt-1 block truncate text-[10px] font-black uppercase tracking-wide">
                {getModuleTitle(module, index)}
              </span>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {modules.map((module, index) => (
            <button
              key={module.id || module._localId}
              onClick={() => {
                nudge(16);
                setActiveIndex(index);
              }}
              className={`rounded-full border px-3 py-2 text-[8px] font-black uppercase tracking-widest ${
                activeIndex === index ? 'border-white text-white shadow-lg' : 'lumirae-opening-pill'
              }`}
              style={activeIndex === index ? { backgroundColor: 'var(--lumirae-accent-primary)' } : undefined}
            >
              Moment {index + 1}
            </button>
          ))}
        </div>
      </div>
      <AnimatePresence mode="wait">
        {activeModule && (
          <motion.div
            key={activeModule.id || activeModule._localId || activeIndex}
            initial={{ opacity: 0, x: 18 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -18 }}
            className="w-full overflow-visible"
          >
            {renderModules([activeModule], 'full_page')}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MemoryJourneyExperience;
